/**
 * P-B.1 长图分段面板(任务详情页,posterKind=LONG_IMAGE 时渲染)。
 *
 * 分段资产(assetMeta.segment)逐块展示,每块一个入口:「改这一块」——
 * 画面调整 / 改内容 / AI 改图 / AI 重画 四件事都在那个编辑器里(见 SegmentEditorDialog),
 * 露哪些 tab 由块类型决定。★2026-08-08 之前这里另有一颗「重出」按钮,与「调整」并列 ——
 * 两个入口做同一类事,而用户最想要的「改这一块的字」两个都办不到。已收敛成一个。
 * 轮询信号 = backend 预置目标分段 PENDING → ai 回写终态;面板内 4s 自轮询直到无 PENDING。
 *
 * 皮肤:全语义 token;硬编码中文口径(V24 迁 i18n)。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, PencilLine, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/shared/lib/utils";
import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Textarea } from "@/shared/ui/textarea";
import { MediaPreviewLightbox } from "@/entities/media-asset/ui/MediaPreviewLightbox";
import { useQuotePreview } from "@/entities/pricing/model/useQuote";
import { grouped as groupedCredits } from "@/entities/scene-card/model/creditRates";
import { posterService, type PosterAssetDto } from "../../api/posterService";
import { PRICE_PENDING_TEXT } from "../../model/free-create/submitGate";
import { longImageBlockRegenQuoteParams } from "../model/blockRegenQuote";
import { STYLE_META } from "../model/blocks";
import { hiddenBlockIdsOf } from "../model/useLongImageSpec";
import { complianceHits } from "../model/complianceHits";
import { SEG_MAX_H, SEG_MIN_H, WIDE_BAND_ASPECT, segmentAspect } from "../model/segmentFrame";
import {
  canRewrapPrompt,
  customerPromptText,
  customerScreenContent,
  rewrapPrompt,
} from "../model/screenPromptView";
import { errorToast } from "@/shared/lib/apiErrors";

const POLL_MS = 4000;
const POLL_CAP_MS = 3 * 60 * 1000;

/**
 * 本屏提示词的**一行摘要**取差异化那段(0910 设计审查:每屏提示词都以完全相同的
 * 「【画幅】竖版 3:4…【画质】…【整屏版式结构】…」样板开头,折叠行只有一行,全被
 * 样板占满、露不到这屏真正不一样的内容)。
 *
 * ★纯展示层:只挪一行摘要从哪儿起念,不动 imagePromptPreview 本身(展开看的是全文,
 *   P3 硬约束=ai 提示词零改动)。认不出「第 N 部分」结构就原样返回,不赌格式。
 */
export function screenPromptGist(raw: string): string {
  const t = raw.trim();
  const m = t.match(/第\s*[0-9一二三四五六七八九十]+\s*部分/);
  return m && m.index != null && m.index > 0 ? t.slice(m.index) : t;
}

/**
 * LI-SCREEN-1:这一段是不是「屏」(一键合屏成品的分段=屏不=块)。
 * 权威判据是 DTO 的 screen 标(api 从 assetMeta 透出);scr{n} 形状兜底 ——
 * ai 侧保证屏 id 刻意不与任何真块同名,所以后端未升级时 console 单独先发也不误判。
 */
export function isScreenSegment(s: Pick<PosterAssetDto, "screen" | "blockId">): boolean {
  return s.screen === true || (!!s.blockId && /^scr\d+$/.test(s.blockId));
}

interface Props {
  taskId: number;
  assets: PosterAssetDto[];
  /** 强制重拉 task+assets(usePosterTaskPolling.refresh)。 */
  onRefresh: () => Promise<void> | void;
  /**
   * Dock 形态(2026-08-07 版面重构):挂在看图台右侧的 400px 列里 —— 一列到底、
   * 整块吃满高度、内容自滚。不给 = 存量的通栏多列网格。
   */
  dense?: boolean;
  /** 左边正读到的那一块(高亮用)。 */
  activeBlockId?: string | null;
  /** 给了就在块名上开「跳到这一块」——上层算不准位置时不会给(见 segmentOffsets)。 */
  onJumpToBlock?: (blockId: string) => void;
  /**
   * 「调整这一块」(2026-08-08 方案 C):打开该分段的画布改 logo / 滤镜。
   *
   * ★长图最终是分段拼出来的,所以每一段就是一张普通产物 —— 改它再重拼,整图就跟着变。
   *   给 asset 而不是 blockId:抽屉要 baseUrl / 真实像素 / 已存的 overlay,那些都在资产上。
   */
  onAdjust?: (asset: PosterAssetDto) => void;
  /**
   * 这一稿真正发给引擎的整包(`readLongImageSpec`)—— 随「回搭建器改这一稿」一起带过去。
   *
   * ★为什么是**带过去**而不是让搭建器自己拉:搭建器的种子全是同步构造参数
   *   (模板/用途卡/蓝图 handoff 都这样),异步塞进去要额外一套 loading 态;
   *   而结果页这边为了「改内容」本来就已经把它拉好了 —— 传过去零成本,
   *   还顺带保证了「搭建器里显示的」与「结果页上显示的」是同一份东西。
   * ★没有它就退回老行为(只带 taskId,搭建器提示对不上稿)。
   */
  editSpec?: Record<string, unknown> | null;
}

export function LongImageSegmentsPanel({
  taskId,
  assets,
  onRefresh,
  dense = false,
  activeBlockId = null,
  onJumpToBlock,
  onAdjust,
  editSpec = null,
}: Props) {
  const queryClient = useQueryClient();
  /**
   * E-09b:已删(hidden)块的分段**不再列出来**。分段资产按契约永不真删(seq = 块序 index+2,
   * 真摘会让后面全部前移),此前列表直接渲全部分段 → 删除后左侧成品图变了、右侧仍是 7 条,
   * 看起来像「删除没生效、刷新又恢复」。hidden 的真相在任务 payload(editSpec.blocks)里,
   * 这里取出来过滤;editSpec 拿不到时退回老行为(不过滤,总比错过滤强)。
   */
  const hiddenBlockIds = useMemo(() => hiddenBlockIdsOf(editSpec), [editSpec]);
  const segments = assets
    .filter((a) => a.segment && a.blockId && !hiddenBlockIds.has(a.blockId))
    .sort((a, b) => a.seq - b.seq);
  const [busyBlock, setBusyBlock] = useState<string | null>(null);
  /** 最近删掉的那一块 —— 撤销条只记一条(「可撤销一次」)。 */
  const [lastDeleted, setLastDeleted] = useState<{ blockId: string; label: string } | null>(null);
  /** LI-SCREEN-2:「重画这一屏」确认层(可选一句要求 + 计费说明)。 */
  const [redrawTarget, setRedrawTarget] = useState<PosterAssetDto | null>(null);
  const [redrawNote, setRedrawNote] = useState("");
  /** 屏卡 P1(0910):可编辑的本屏提示词(预填 imagePromptPreview);改过 = 整段覆盖发 promptOverride。 */
  const [redrawPrompt, setRedrawPrompt] = useState("");
  const [redrawSubmitting, setRedrawSubmitting] = useState(false);
  /**
   * K3(0927 第十三批 D2):「重画这一屏」确认层要亮出**这一次多少钱** —— 此前只有一句
   * 「按一次出图计费」没有数字,用户按下去之前不知道是 28 还是 210。
   *
   * ★口径与「改这一块」的 AI 重画完全同一份(`blockRegenQuote`,api `regenerateLongImageBlock` 对屏 id
   *   `scr{n}` 走同一条端点):SKU longimage.render × 整包 provider × `canvas.scale` 档(1K/2K/4K)× 1 张。
   *   档位取 `editSpec.canvas.scale`:一键任务落库的是 ai 投影后的 v1 整包(api
   *   `LongImageOneClickOrchestrationService.startGeneration` → `projectCanvasToRequest`,
   *   `canvas.scale = canvasMeta.scale`),api 余额精检 `PosterCreditPrecheck.longImageTier` 与 ai
   *   `_run_regen_screen` 的 `tier = _SCALE_TO_TIER[req.canvas.scale]` 都从这一格取 —— 报价与实扣同一根针。
   * ★只在确认层打开时问价(dryRun),关了就不问;整包没到 / 报价口失败 → 「价格待系统核算」,
   *   **不兜数、不猜档**(一个说得斩钉截铁却错档的数比不显示更糟)。
   */
  const redrawQuote = useQuotePreview(
    redrawTarget ? longImageBlockRegenQuoteParams(editSpec) : null
  );
  const redrawPriceText =
    redrawQuote.credits != null
      ? `约 ${groupedCredits(redrawQuote.credits)} 积分`
      : redrawQuote.loading
        ? "报价中…"
        : PRICE_PENDING_TEXT;
  // BP-01:分段灯箱(带左右翻);index 按 segments 顺序,不用 seq 直接当下标(seq 不保证从 0 连续)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const pollStartRef = useRef<number | null>(null);

  const anyPending = segments.some((s) => s.status === "PENDING");
  // codex 修:重出刚提交时后端 PENDING 可能还没写回首次 refresh —— busyBlock 也驱动轮询,
  // 否则 anyPending 恒 false → 轮询不起、按钮永冻。
  const watching = anyPending || busyBlock != null;

  useEffect(() => {
    if (!watching) {
      pollStartRef.current = null;
      return;
    }
    pollStartRef.current = pollStartRef.current ?? Date.now();
    // codex 修:递归 setTimeout + await(慢 refresh 不重入,不造轮询风暴)
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      if (stopped) return;
      if (pollStartRef.current && Date.now() - pollStartRef.current > POLL_CAP_MS) return;
      try {
        await onRefresh();
      } catch {
        // 单次失败继续下一轮
      }
      if (!stopped) timer = setTimeout(() => void tick(), POLL_MS);
    };
    timer = setTimeout(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [watching, onRefresh]);

  // busyBlock 收尾:分段真进入 PENDING(状态接管 spinner)或 30s 宽限后释放
  useEffect(() => {
    if (busyBlock == null) return;
    if (anyPending) {
      setBusyBlock(null);
      return;
    }
    const t = setTimeout(() => setBusyBlock(null), 30_000);
    return () => clearTimeout(t);
  }, [busyBlock, anyPending]);

  /**
   * 任务 16 ·「删掉这块」—— 零出图,只把剩下的分段重新拼一遍。
   *
   * ★后端把它实现成**打 hidden 标记**而不是真删:分段 seq 契约是「块序 index+2」,
   *   真摘掉一块会让后面每块 seq 全部前移、而它们的字节一个都没变。
   *   于是「撤销」是白拿的 —— 把标记翻回去再拼一次就行,这里也就敢做撤销。
   * ★二次确认:删块会改掉用户手里那张成品,不是可以随手点的操作。
   */
  const setHidden = async (blockId: string, hidden: boolean, blockLabel: string) => {
    if (
      hidden &&
      !window.confirm(`把「${blockLabel}」从这张长图里删掉?\n\n不花积分,删完可以撤销一次。`)
    ) {
      return;
    }
    setBusyBlock(blockId);
    try {
      await posterService.setLongImageBlockHidden(taskId, blockId, hidden);
      // E-09b:hidden 的真相在 spec(任务 payload)里,列表过滤靠它 —— 不失效这条查询,
      // 右侧列表要等 30s staleTime 才追上左侧成品图。
      await queryClient.invalidateQueries({ queryKey: ["longimage-spec", taskId] });
      setLastDeleted(hidden ? { blockId, label: blockLabel } : null);
      toast({
        title: hidden ? "已删掉这块,正在重新拼图" : "已把这块加回来",
        description: hidden ? "没有重新出图,所以不花积分。可以撤销。" : "重新拼图中。",
      });
      await onRefresh();
    } catch (e) {
      setBusyBlock(null);
      toast({
        variant: "destructive",
        title: hidden ? "删块失败" : "撤销失败",
        description: errorToast(e).description,
      });
    }
  };

  /**
   * LI-SCREEN-2 ·「重画这一屏」—— 合屏成品的屏级重出(骑单块 regen 的同一条端点,
   * blockId=scr{n};api/ai 按屏寻址)。真出图一次,所以有确认层,不是随手点的操作。
   */
  const submitRedraw = async () => {
    if (!redrawTarget?.blockId) return;
    const blockId = redrawTarget.blockId;
    setRedrawSubmitting(true);
    // 屏卡 P1:提示词被用户改过 → 整段覆盖(逐字直达引擎),一句要求那条就不再叠着发
    // (override 本身就是用户的完整意图,两个都发等于同一诉求进两次)。
    // 1005:用户只看得到/改得了内容段;改过就把原系统段拼回,整段发 override(ai 逐字用)。
    const raw = redrawTarget.imagePromptPreview;
    const original = customerPromptText(raw);
    const edited = redrawPrompt.trim();
    const promptOverride =
      edited && edited !== original && canRewrapPrompt(raw) ? rewrapPrompt(raw, edited) : null;
    try {
      await posterService.regenerateLongImageBlock(taskId, blockId, {
        ...(promptOverride
          ? { promptOverride }
          : redrawNote.trim()
            ? { instruction: redrawNote.trim() }
            : {}),
      });
      setBusyBlock(blockId);
      setRedrawTarget(null);
      setRedrawNote("");
      setRedrawPrompt("");
      toast({
        title: "已开始重画这一屏",
        description: "只重画这一屏(按一次出图计费),其余屏一个字节不动。出好后自动替换。",
      });
      await onRefresh();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "重画失败",
        description: errorToast(e).description,
      });
    } finally {
      setRedrawSubmitting(false);
    }
  };

  const lightboxItems = segments
    .filter((x) => x.baseUrl || x.thumbUrl)
    .map((x) => ({
      url: (x.baseUrl ?? x.thumbUrl) as string,
      kind: "image" as const,
      title: x.blockName ?? x.styleTag ?? `分段 ${x.seq}`,
    }));
  const openLightboxAt = (seq: number) => {
    const i = segments.filter((x) => x.baseUrl || x.thumbUrl).findIndex((x) => x.seq === seq);
    setLightboxIndex(i >= 0 ? i : 0);
  };

  if (segments.length === 0) return null;

  // D4 可见化:合规降级此前只落审计不往上冒 —— 用户拿到被改过词的图,不知道谁改的、改了哪几处。
  const compBlocks = segments
    .map((s) => ({ seg: s, hits: complianceHits(s.compliance) }))
    .filter((x) => x.hits.length > 0);
  const compTotal = compBlocks.reduce((n, x) => n + x.hits.length, 0);

  return (
    <Card className={cn("shadow-sm", dense && "flex h-full min-h-0 flex-col")}>
      <CardHeader className={cn("pb-2", dense && "shrink-0")}>
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
          {/* ★词表统一(2026-09-08):一键合屏成品的分段就是「屏」,与预览角标「第 N/M 屏」、
              每条「重画这一屏 / AI 整屏」同口径;非一键(手动搭/逐块)才叫「区块」。
              原来固定写「分段区块」,屏成品里三处词面打架(屏 / 区块 / 分段)。 */}
          {editSpec?.oneClick === true ? "分屏" : "区块"}({segments.length})
          {compTotal > 0 && (
            <span
              className="ml-2 rounded-full bg-amber-500/10 px-2 py-0.5 text-2xs font-normal text-amber-700 dark:text-amber-400"
              title="按广告法把绝对化/功效风险词换成了合规说法。想保留原词请回搭建器改文案再重出。"
            >
              合规降级 {compTotal} 处
            </span>
          )}
          {/* ★出图之后想改一句话,以前只剩「整张从头再来、再付一次全款」这一条路。
              ★2026-08-08:这个链接**此前从来没回填过** —— 搭建器的草稿是按人存一份的
              (`useCanvasBuilder` 拉 listDrafts 取第一份能解析的,与 taskId 无关),
              用户出过图 A 又新做了一稿 B,从 A 点进去躺着的是 B,只会弹一句
              「这里打开的不是那一稿」。现在把**这一稿本身**带过去当种子。 */}
          <Link
            to="/ai-haibao/detail-long"
            state={{ longImageEditTask: taskId, longImageEditSpec: editSpec }}
            data-longimage-back-to-builder
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-border/60 px-2.5 py-1 text-2xs font-normal text-muted-foreground hover:text-foreground"
            title={
              editSpec?.oneClick === true
                ? "回搭建器改文案/画面,改完整张重新出图(仍按屏出图结算)"
                : "回搭建器改文案/画面,改完只重出改动的那一块"
            }
          >
            <PencilLine className="h-3 w-3" />
            回搭建器改这一稿
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className={cn("space-y-2", dense && "min-h-0 flex-1 overflow-y-auto")}>
        {lastDeleted && (
          <div
            className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-[12px]"
            data-undo-delete
          >
            <span className="min-w-0 truncate text-muted-foreground">
              已从这张长图里删掉「<b className="text-foreground">{lastDeleted.label}</b>」
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 shrink-0 gap-1 px-2 text-2xs text-primary"
              disabled={anyPending || busyBlock != null}
              onClick={() => void setHidden(lastDeleted.blockId, false, lastDeleted.label)}
            >
              <Undo2 className="h-3 w-3" />
              撤销
            </Button>
          </div>
        )}
        {/* items-start:卡按各自内容高度收住,不被同行最高的那张拉齐 —— 高度不再齐了,
            但每张卡的画面就是它在整图里的样子,这正是这个面板要回答的问题。 */}
        <ul
          className={cn(
            "grid items-start gap-2",
            // Dock 只有 400px 宽:多列会把每块压到 90px,看不出是哪一块。
            dense ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          )}
        >
          {segments.map((s) => {
            const meta = s.styleTag ? STYLE_META[s.styleTag] : undefined;
            const isImage = meta?.primitive === "image";
            /* LI-SCREEN-1:屏卡不吃块的那套定性 —— 它是一张真金白银 AI 整屏出的图,
               此前按 styleTag=body 落进「模板块/不花积分」角标是错误定性;单块重出/
               删块对它必 400(seq 契约不成立),按钮一并收掉,换「重画这一屏」。 */
            const isScreen = isScreenSegment(s);
            const pending = s.status === "PENDING" || busyBlock === s.blockId;
            const failed = s.status === "FAILED";
            const aspect = segmentAspect(s);
            return (
              <li
                key={s.blockId}
                data-segment-active={activeBlockId === s.blockId ? "true" : undefined}
                className={cn(
                  "rounded-lg border p-2",
                  // Dock 里排成**一行**:缩略在左、块名与动作在右。
                  // ★为什么不沿用通栏那种大图卡:1440×900 实测每块 309px 高、7 块 1772px,
                  //   而 Dock 可视只有 418 —— 一屏看得见 1.3 块,「找到那一块再动它」这件事
                  //   全靠滚。这一列的活是**定位 + 动手**,不是欣赏画面(那是左边看图台的活,
                  //   点缩略图照样开灯箱看原尺寸)。
                  dense ? "flex items-start gap-2.5" : "space-y-1.5",
                  // 失败块整卡染色:此前失败只体现在卡底一行 10px 红字,一眼扫过去看不见
                  failed ? "border-destructive/50 bg-destructive/5" : "border-border bg-card",
                  // 横条占两列只在多列网格里有意义;Dock 是一列,写了也只是占满那一列
                  !dense && aspect >= WIDE_BAND_ASPECT && "col-span-2",
                  // 左边正读到这一块 —— 高亮是「你在这」,不是选中态
                  activeBlockId === s.blockId && "ring-2 ring-primary/60"
                )}
              >
                <div
                  className={cn(
                    "relative flex items-center justify-center overflow-hidden rounded bg-muted",
                    dense ? "w-16 shrink-0" : "w-full"
                  )}
                  /* ★aspectRatio + maxHeight 一起给:比例撑出真实画幅,超高的图块被上限压住时
                     由 object-contain 留边 —— 任何情况下都不裁,裁了就认不出是哪一块(D3)。
                     ★w-full 不能省:块级盒 width:auto + aspect-ratio 且高度被 maxHeight 定死时,
                     浏览器改成**由高反推宽** → 失败卡的画框比卡本身窄一截(实测 180 vs 230)。 */
                  style={
                    dense
                      ? // 紧凑行:宽定死 64,高按真实画幅反算并封顶 72 —— 认得出是哪一块就够了
                        { aspectRatio: `${aspect}`, minHeight: 40, maxHeight: 72 }
                      : { aspectRatio: `${aspect}`, minHeight: SEG_MIN_H, maxHeight: SEG_MAX_H }
                  }
                >
                  {s.thumbUrl ? (
                    /* BP-01:分段块实测只渲染到 16~22%,等于没显示 —— 必须能点开看原尺寸。
                       灯箱带左右翻,一次看完所有分段(不用退出去再点下一块)。 */
                    <button
                      type="button"
                      onClick={() => openLightboxAt(s.seq)}
                      className="block h-full w-full cursor-zoom-in"
                      aria-label={`看大图:${s.blockName ?? s.styleTag ?? "分段"}`}
                      data-testid={`segment-open-${s.blockId}`}
                    >
                      <img
                        src={s.thumbUrl}
                        alt={s.blockName ?? s.styleTag ?? "分段"}
                        className="h-full w-full object-contain"
                      />
                    </button>
                  ) : (
                    <div
                      className={cn(
                        "flex h-full items-center justify-center gap-1 px-2 text-center text-xs",
                        failed ? "text-destructive" : "text-muted-foreground"
                      )}
                    >
                      {failed && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                      {failed ? "这块没出成,整图里是占位条" : "无预览"}
                    </div>
                  )}
                  {pending && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    </div>
                  )}
                </div>
                {/* dense:右侧一列(块名/动作/原文);通栏:原来的纵向堆叠 */}
                <div className={cn(dense ? "min-w-0 flex-1 space-y-1" : "space-y-1.5")}>
                  {/* dense(400px 窄 dock):屏名单独占一整行不被三个操作挤截成「第1…」,
                      操作按钮(画面调整/重画这一屏/AI 整屏)落到第二行(2026-09-10 排版修)。 */}
                  <div
                    className={cn(
                      "flex gap-1",
                      dense ? "flex-col items-start" : "items-center justify-between"
                    )}
                  >
                    {/* ★块名可点 = 跳到整图里的这一块。13 屏的长图靠滚是找不到「规格参数」的,
                      而位置算得准(分段高度之和 = 整图高,实测一像素不差)。
                      算不准时上层不传 onJumpToBlock,这里就退回纯文字 —— 跳错位比不能跳更糟。 */}
                    {onJumpToBlock && s.blockId ? (
                      <button
                        type="button"
                        onClick={() => onJumpToBlock(s.blockId!)}
                        data-segment-jump={s.blockId}
                        title="跳到整图里的这一块"
                        className={cn(
                          "min-w-0 truncate text-left text-xs underline-offset-2 hover:text-primary hover:underline",
                          dense && "w-full"
                        )}
                      >
                        {s.blockName || meta?.label || s.styleTag}
                        <span className="ml-1 text-2xs text-muted-foreground">
                          {isScreen ? "" : (meta?.badge ?? "")}
                        </span>
                      </button>
                    ) : (
                      <span className={cn("min-w-0 truncate text-xs", dense && "w-full")}>
                        {s.blockName || meta?.label || s.styleTag}
                        <span className="ml-1 text-2xs text-muted-foreground">
                          {isScreen ? "" : (meta?.badge ?? "")}
                        </span>
                      </span>
                    )}
                    <span className="flex shrink-0 items-center gap-1">
                      {/* ★「重出」并进「改这一块」(2026-08-08):此前块行上「调整」与「重出」
                        是两个入口做同一类事 —— 前者改画面、后者换内容,而用户最常要的
                        「改这一块的字」两个都办不到,只能绕去搭建器(那条路又回填不上)。
                        现在四件事(画面调整 / 改内容 / AI 改图 / AI 重画)收在一个编辑器里,
                        按块类型露哪些 tab 由编辑器自己判 —— 块行不再自己维护一份「谁能干什么」。
                        ★角标留着:它回答的是「这块花不花钱」,那与入口收敛无关。 */}
                      {isScreen ? (
                        <>
                          {/* ★屏也给「画面调整」(2026-09-09 老板「长图没法加 logo/二维码」):
                              0905 LI-SCREEN-1 收掉「改这一块」时,把只活在那个编辑器里的
                              挂 logo/二维码/滤镜一并带走了 —— 排屏时代的长图从此无处挂标。
                              编辑器对屏天然只露「画面调整」tab(blockSpec 查不到),叠标走
                              saveAssetOverlay + restitch,与重画这一屏共用的屏级 seq 契约
                              (ai _screens_seq_contract)0905 起就支持。 */}
                          {s.status === "SUCCESS" && onAdjust && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 gap-1 px-2 text-2xs"
                              disabled={pending || anyPending}
                              data-screen-adjust={s.blockId}
                              onClick={() => onAdjust(s)}
                            >
                              画面调整
                            </Button>
                          )}
                          {s.status === "SUCCESS" && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 gap-1 px-2 text-2xs"
                              disabled={pending || anyPending}
                              data-screen-redraw={s.blockId}
                              onClick={() => {
                                setRedrawNote("");
                                // 1005:编辑框只放对客内容段(第 N 部分),系统段提交时拼回
                                setRedrawPrompt(customerPromptText(s.imagePromptPreview));
                                setRedrawTarget(s);
                              }}
                            >
                              <RefreshCw className="h-3 w-3" />
                              重画这一屏
                            </Button>
                          )}
                          <span
                            className="rounded bg-primary/10 px-1 py-0.5 text-2xs font-medium text-primary"
                            title="一键长图按屏出图:相邻几段合成一屏一次画。重画这一屏按一次出图计费;想改文字内容回搭建器改稿后重新出图"
                          >
                            AI 整屏
                          </span>
                        </>
                      ) : (
                        <>
                          {onAdjust && s.status === "SUCCESS" && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 gap-1 px-2 text-2xs"
                              disabled={pending || anyPending}
                              data-segment-adjust={s.blockId}
                              onClick={() => onAdjust(s)}
                            >
                              <PencilLine className="h-3 w-3" />
                              改这一块
                            </Button>
                          )}
                          {isImage ? (
                            <span
                              className="rounded bg-primary/10 px-1 py-0.5 text-2xs font-medium text-primary"
                              title="这块由 AI 出图 —— 改内容 / AI 改图 / AI 重画 都按单块计一笔出图积分"
                            >
                              AI 图块
                            </span>
                          ) : (
                            <span
                              className="rounded bg-muted px-1 py-0.5 text-2xs text-muted-foreground"
                              title="模板确定性渲染 —— 改文案只重渲重拼,不花积分"
                            >
                              模板块
                            </span>
                          )}
                          {/* 删块对两类块都开放:多余就是多余,与它由谁渲染无关。零出图成本。
                              屏卡不给删:合屏成品的删块重拼在后端是闸掉的(seq 契约不成立)。 */}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                            disabled={pending || anyPending || segments.length <= 1}
                            title={
                              segments.length <= 1
                                ? "只剩这一块了 —— 全删掉请直接删这个任务"
                                : "从这张长图里删掉这块(不花积分,可撤销)"
                            }
                            data-delete-block={s.blockId}
                            onClick={() =>
                              void setHidden(
                                s.blockId!,
                                true,
                                s.blockName || meta?.label || s.styleTag || "这块"
                              )
                            }
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </span>
                  </div>
                  {/* ★这块的原文(2026-07-30,7.29 反馈任务 15)。
                    缺陷:长图分支不走 AssetEditor,也就没有海报那侧的 TaskSettingsFold ——
                    成品出来图文不符时,用户看得到图却**看不到这块当初写的是什么**,
                    只能回搭建器逐块对。数据后端一直有(promptUsed → imagePromptPreview),
                    纯展示缺口。默认一行摘要,点开看全文,不占版面。 */}
                  {customerScreenContent(s.imagePromptPreview) ? (
                    <details className="group" data-segment-prompt={s.blockId}>
                      <summary className="cursor-pointer list-none truncate text-2xs text-muted-foreground hover:text-foreground">
                        <span className="mr-0.5 inline-block transition-transform group-open:rotate-90">
                          ›
                        </span>
                        本屏内容:{screenPromptGist(customerScreenContent(s.imagePromptPreview))}
                      </summary>
                      <p className="mt-1 whitespace-pre-line rounded bg-muted/50 p-1.5 text-2xs leading-relaxed text-muted-foreground">
                        {customerScreenContent(s.imagePromptPreview)}
                      </p>
                    </details>
                  ) : null}
                  {failed && (
                    <p
                      className="truncate text-2xs text-destructive"
                      title={maskVendor(s.failureReason) ?? ""}
                    >
                      {maskVendor(s.failureReason) ?? "失败"}
                    </p>
                  )}
                  {complianceHits(s.compliance).map((h) => (
                    <p
                      key={h.raw}
                      className="truncate text-2xs text-amber-700 dark:text-amber-400"
                      title={`按广告法降级:「${h.raw}」→「${h.safe}」`}
                    >
                      合规:{h.raw} → {h.safe}
                    </p>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
        {segments.some(isScreenSegment) ? (
          <p className="text-2xs text-muted-foreground">
            这张长图按「屏」出图(相邻几段合成一屏一次画)。「重画这一屏」只重画那一屏并重拼整图,
            按一次出图计费;想改文字内容,回搭建器改稿后重新出图。
          </p>
        ) : (
          <p className="text-2xs text-muted-foreground">
            「改这一块」里改画面 / 改文案 / AI 改图 / AI 重画 —— 都只动这一块并重拼整图,
            其余分段一个字节不动。文案块改字零积分,图块重出按单块计一笔出图。
          </p>
        )}
      </CardContent>
      <MediaPreviewLightbox
        open={lightboxIndex !== null}
        onOpenChange={(o) => !o && setLightboxIndex(null)}
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
      />
      {/* LI-SCREEN-2:「重画这一屏」确认层 —— 真出图一次(花钱),所以有确认,不直接发。 */}
      <Dialog
        open={redrawTarget != null}
        onOpenChange={(o) => {
          if (!o && !redrawSubmitting) setRedrawTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[16px]">
              重画{redrawTarget?.blockName || "这一屏"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              只重画这一屏并重拼整图,其余屏一个字节不动;按一次出图计费,
              {/* K3:数字来自报价口(dryRun),与实扣同档;取不到就明说「价格待系统核算」 */}
              <span data-screen-redraw-price className="font-medium text-foreground">
                {redrawPriceText}
              </span>
              。不动下面的提示词则文字内容不变;直接改提示词 = 这一屏完全按你写的画。
            </DialogDescription>
          </DialogHeader>
          <div>
            <div className="mb-1 text-[12.5px] text-muted-foreground">
              这次想怎么改(可不填,不填就换一版画法)
            </div>
            <Textarea
              value={redrawNote}
              onChange={(e) => setRedrawNote(e.target.value)}
              placeholder="例:背景换成浅色,排版更疏朗"
              rows={2}
              maxLength={200}
              className="text-[13px]"
            />
          </div>
          {/* 屏卡 P1(0910 老板拍板):把编排后真实发给引擎的提示词交到用户手里 ——
              预填本屏 prompt_used,改过就整段接管(promptOverride,ai 逐字用,
              上面那句"要求"不再叠发)。默认折叠:多数人用一句话路,别拿大段吓人。 */}
          {canRewrapPrompt(redrawTarget?.imagePromptPreview) ? (
            <details data-screen-prompt-editor>
              <summary className="cursor-pointer list-none text-[12.5px] text-muted-foreground hover:text-foreground">
                高级:直接改这一屏要画的内容和文字
              </summary>
              <Textarea
                value={redrawPrompt}
                onChange={(e) => setRedrawPrompt(e.target.value)}
                rows={8}
                maxLength={8000}
                className="mt-1.5 font-mono text-[12px] leading-relaxed"
                data-screen-prompt-textarea
              />
              <p className="mt-1 text-2xs text-muted-foreground">
                这里是这一屏的画面内容与要印的文字;改了就按你写的重画,文字准确性请自行核对。
              </p>
            </details>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={redrawSubmitting}
              onClick={() => setRedrawTarget(null)}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={redrawSubmitting}
              data-screen-redraw-confirm
              onClick={() => void submitRedraw()}
            >
              {redrawSubmitting && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              {/* K3:按钮上带价,与「改这一块」AI 重画按钮同形制(RegenerateTab costSuffix);
                  没拿到数就只写动作 —— 描述行那句已经说了「价格待系统核算」,不重复。 */}
              {redrawQuote.credits != null
                ? `重画这一屏 · 约 ${groupedCredits(redrawQuote.credits)} 积分`
                : "重画这一屏"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
